import React, { useState } from 'react'
import { Form, Button, Card, Alert, Container, Row, Col } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { supabase } from '../services/supabase'

// ✅ CORRECTION : Déplacer SimpleLayout EN DEHORS du composant Register
const SimpleLayout = ({ children }) => (
  <div className="min-vh-100 bg-light d-flex align-items-center">
    <Container className="py-4">
      {children}
    </Container>
  </div>
)

export default function Register() {
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite')
  const tokenValid = !!inviteToken

  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    birthDate: '',
    nationality: '',
    rgpdConsent: false
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [participant, setParticipant] = useState(null)

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Validation
    if (!inviteToken) {
      setError("Lien d'invitation manquant.")
      setLoading(false)
      return
    }

    if (!formData.lastName || !formData.firstName || !formData.birthDate || !formData.nationality) {
      setError("Tous les champs sont obligatoires.")
      setLoading(false)
      return
    }

    if (!formData.rgpdConsent) {
      setError("Vous devez accepter le RGPD et le règlement.")
      setLoading(false)
      return
    }

    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        birth_date: formData.birthDate,
        nationality: formData.nationality,
        rgpd_consent: true,
        rules_accepted: true
      }

      const { data, error: insertError } = await supabase
        .from('participants')
        .insert([payload])
        .select()
        .single()

      if (insertError) throw new Error(insertError.message)

      setParticipant(data)
      setSuccess(true)
    } catch (err) {
      setError(err.message || "Une erreur est survenue lors de l'inscription")
    }

    setLoading(false)
  }

  if (!tokenValid) {
    return (
      <SimpleLayout>
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow-sm">
              <Card.Body className="text-center py-5">
                <div style={{ fontSize: '4rem' }} className="text-danger mb-3">❌</div>
                <h4>Lien d'invitation invalide</h4>
                <p className="text-muted">Veuillez contacter le staff pour obtenir un nouveau lien.</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </SimpleLayout>
    )
  }

  if (success && participant) {
    return (
      <SimpleLayout>
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow-sm">
              <Card.Body className="text-center">
                <div style={{ fontSize: '4rem' }} className="text-success mb-3">✅</div>
                <h3 className="text-success">Inscription réussie !</h3>
                <p className="lead mb-3">
                  Bienvenue <strong>{participant.first_name} {participant.last_name}</strong>
                </p>

                <div className="my-4 p-4 bg-light rounded">
                  <p className="mb-3"><strong>Votre QR code personnel :</strong></p>
                  <QRCodeSVG value={participant.token} size={200} includeMargin />
                  <p className="text-muted small mt-3">
                    Conservez ce QR code pour marquer votre présence à chaque session.
                  </p>
                </div>

                <Button variant="primary" size="lg" onClick={() => window.print()}>
                  🖨️ Imprimer le QR Code
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </SimpleLayout>
    )
  }

  return (
    <SimpleLayout>
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          
          {/* En-tête */}
          <div className="text-center mb-4">
            <div className="mb-3" style={{ fontSize: '4rem' }}>🥊</div>
            <h1 className="fw-bold text-primary mb-2">Street Sport Luxembourg</h1>
            <h4 className="text-muted">Inscription rapide</h4>
          </div>

          {/* Formulaire */}
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              
              {error && (
                <Alert variant="danger" className="mb-3">
                  <strong>Erreur :</strong> {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Nom *</Form.Label>
                  <Form.Control 
                    name="lastName" 
                    value={formData.lastName} 
                    onChange={handleChange} 
                    required 
                    size="lg"
                    placeholder="Ex: Dupont"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Prénom *</Form.Label>
                  <Form.Control 
                    name="firstName" 
                    value={formData.firstName} 
                    onChange={handleChange} 
                    required 
                    size="lg"
                    placeholder="Ex: Jean"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Date de naissance *</Form.Label>
                  <Form.Control 
                    type="date" 
                    name="birthDate" 
                    value={formData.birthDate} 
                    onChange={handleChange} 
                    required 
                    size="lg"
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Pays *</Form.Label>
                  <Form.Control 
                    name="nationality" 
                    value={formData.nationality} 
                    onChange={handleChange} 
                    required 
                    size="lg"
                    placeholder="Ex: Luxembourg"
                  />
                </Form.Group>

                <Card className="bg-light mb-3">
                  <Card.Body>
                    <h6 className="mb-2">Consentement RGPD et Règlement</h6>
                    <div className="small text-muted mb-2">
                      <strong>Protection des données (RGPD) :</strong>
                      <p className="mb-2">
                        Conformément au Règlement Général sur la Protection des Données (RGPD), 
                        vos données personnelles seront traitées avec la plus grande confidentialité. 
                        Elles seront utilisées uniquement dans le cadre du projet Streetsport et 
                        ne seront en aucun cas transmises à des tiers sans votre consentement explicite.
                      </p>
                      
                      <strong>Règlement du projet Streetsport :</strong>
                      <ul className="mb-0 ps-3">
                        <li>Respect des autres participants</li>
                        <li>Comportement civilisé et fair-play</li>
                        <li>Interdiction de violence (entraîne l'exclusion)</li>
                        <li>Respect du matériel et des lieux</li>
                        <li>Interdiction de drogues et d'alcool</li>
                        <li>Respect des horaires d'entraînement</li>
                        <li>Non-utilisation des techniques apprises en dehors du cadre sportif</li>
                      </ul>
                    </div>
                    
                    <Form.Check
                      type="checkbox"
                      id="rgpd-consent"
                      name="rgpdConsent"
                      checked={formData.rgpdConsent}
                      onChange={handleChange}
                      label="J'accepte le traitement de mes données personnelles conformément au RGPD et je m'engage à respecter le règlement du projet Streetsport."
                      required
                      className="mt-2"
                    />
                  </Card.Body>
                </Card>

                <Button
                  variant="success"
                  type="submit"
                  className="w-100 py-3"
                  disabled={loading}
                  size="lg"
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Inscription en cours...
                    </>
                  ) : (
                    "✅ S'inscrire"
                  )}
                </Button>
              </Form>

              <div className="text-center mt-3">
                <small className="text-muted">
                  Vous pourrez compléter votre profil après l'inscription
                </small>
              </div>
            </Card.Body>
          </Card>

        </Col>
      </Row>
    </SimpleLayout>
  )
}